import React, { useContext, useEffect, useState } from "react";
import { Popover } from "rizzui/popover";
import DotsVertical from "../../assets/dotsVertitical";
import { useLocation, useNavigate } from "react-router";
import { Button } from "rizzui/button";
import { useDrawer } from "../../hooks/useDrawer";
import RightDrawer from "../../layout/appLayout/rightDrawer";
import axios from "axios";
import { baseUrl } from "../../modules/account/login";
import { AuthContext } from "../../context/authContext";
import { useQueryClient } from "@tanstack/react-query";
import { useModal } from "../../hooks/useModal";
import DeleteNumber from "../deleteNumber";
import { useNotifcication } from "../../hooks/useNotification";
import CampaignTestModal from "../campaignTest";
import CallDrawer from "../callDrawer";

export default function TableMenu({ row, page }) {
  const [isOpen, setIsOpen] = useState(true);
  const pathname = useLocation().pathname.replace("/", "");
  const { openDrawer, closeDrawer } = useDrawer();
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openModal } = useModal();
  const { openNotification } = useNotifcication();

  const handleClick = () => {
    if (page === "knowledgebase") {
      if (row?.original?.documentUrl) navigate(row.original.documentUrl);
    } else if (page === "calls") {
      setIsOpen(false);
      openDrawer({
        view: <CallDrawer row={row} />,
        placement: "right",
        containerClassName: "w-full md:!w-[435px]",
      });
    } else {
      setIsOpen(false);
      openDrawer({
        view: <RightDrawer row={row} onClose={closeDrawer} location={page} />,
        placement: "right",
        containerClassName: "w-full md:!w-[435px]",
      });
    }
  };

  const downloadKnowledgebase = async (id) => {
    try {
      const response = await axios.post(
        `${baseUrl}/knowledgebase/download/${id}`,
        {},
        {
          headers: { Authorization: AuthStr },
        }
      );
      const data = response.data?.data;
      return data?.download_url ?? "#";
    } catch (e) {
      return e;
    }
  };

  const download = async (id) => {
    const url = await downloadKnowledgebase(id);
    setIsOpen(false);
    if (url) window.open(url, "_blank");
  };

  const deleteCampaign = () => {
    const id = row?.original?._id;
    if (id) {
      setIsOpen(false);
      axios
        .delete(`${baseUrl}/campaigns/${id}`, {
          headers: { Authorization: AuthStr },
        })
        .then((response) => {
          const data = response.data;
          if (response?.status === 201) {
            openNotification({
              status: "success",
              message: "Campaign deleted",
            });
            queryClient.invalidateQueries({ queryKey: ["campaigns", token] });
          }
        })
        .catch((e) => {
          if (e?.response?.data?.statusCode === 401)
            navigate("/account/logout");
          return e;
        });
    }
  };

  const deleteLead = () => {
    const id = row?.original?._id;
    if (id) {
      setIsOpen(false);
      axios
        .delete(`${baseUrl}/leads/${id}`, {
          headers: { Authorization: AuthStr },
        })
        .then((response) => {
          if (response?.status === 200) {
            openNotification({
              status: "success",
              message: "Contact deleted",
            });
            queryClient.invalidateQueries({
              predicate: (query) => query.queryKey[0] === "leads",
            });
          }
        })
        .catch((e) => {
          if (e?.response?.data?.statusCode === 401)
            navigate("/account/logout");
          return e;
        });
    }
  };

  const deleteDocument = () => {
    const id = row?.original?.id;
    if (id) {
      setIsOpen(false);
      axios
        .delete(`${baseUrl}/knowledgebase/${id}`, {
          headers: { Authorization: AuthStr },
        })
        .then((response) => {
          const data = response.data;
          if (response?.status === 200) {
            openNotification({
              status: "success",
              message: "File deleted",
            });
            queryClient.invalidateQueries({
              queryKey: ["knowledgebase", token],
            });
          }
        })
        .catch((e) => {
          if (e?.response?.data?.statusCode === 401)
            navigate("/account/logout");

          openNotification({
            status: "error",
            message: "File not deleted, try again",
          });
          return e;
        });
    }
  };

  const pauseResumeCampaign = () => {
    const uri = row?.original?.is_paused ? "resume_campaign" : "pause_campaign";
    const id = row?.original?._id;
    if (id && uri) {
      setIsOpen(false);
      axios
        .put(
          `${baseUrl}/campaigns/${uri}/${id}`,
          { id: row?.original?._id },
          { headers: { Authorization: AuthStr } }
        )
        .then((response) => {
          console.log(response);
          const data = response.data;
          if (response?.status === 201) {
            queryClient.invalidateQueries({ queryKey: ["campaigns", token] });
          }
        })
        .catch((e) => {
          if (e?.response?.data?.statusCode === 401)
            navigate("/account/logout");
          return e;
        });
    }
  };

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);
  return (
    <Popover
      shadow="sm"
      placement="bottom-end"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <Popover.Trigger>
        <Button className="bg-inherit hover:bg-inherit dark:bg-inherit dark:hover:bg-inherit p-0 rounded-none text-primary-dark">
          <DotsVertical className={"text-primary-dark"} />
        </Button>
      </Popover.Trigger>

      <Popover.Content className="z-[9999] p-[8px] rounded-[8px] dark:bg-[#191919] [&>svg]:dark:fill-[#191919] border-[.5px]">
        <div className="bg-inherit !w-[220px]">
          {page === "calls" && (
            <Button
              onClick={handleClick}
              variant="text"
              className="text-primary-dark p-[16px] h-[44px] font-switzer text-[14px] font-[400] leading-[22px] bg-inherit hover:dark:!bg-[#303236] hover:bg-gray-100 w-full justify-start"
            >
              View details
            </Button>
          )}

          {page === "leads" && (
            <>
              {" "}
              <Button
                onClick={handleClick}
                variant="text"
                className="text-primary-dark p-[16px] h-[44px] font-switzer text-[14px] font-[400] leading-[22px] bg-inherit hover:dark:!bg-[#303236] hover:bg-gray-100 w-full justify-start"
              >
                View contact details
              </Button>
              <Button
                onClick={deleteLead}
                variant="text"
                className="text-primary-dark p-[16px] h-[44px] font-switzer text-[14px] font-[400] leading-[22px] bg-inherit hover:dark:!bg-[#303236] hover:bg-gray-100 w-full justify-start"
              >
                Delete contact
              </Button>
            </>
          )}

          {page === "projects" && (
            <>
              {row.original.campaign_creation_percent_complete === 100 &&
                row.original.is_active && (
                  <Button
                    onClick={() => {
                      setIsOpen(false);
                      openModal({
                        view: (
                          <CampaignTestModal campaign_id={row?.original?._id} />
                        ),
                      });
                    }}
                    disabled={
                      row.original.is_stopped || row.original.is_completed
                    }
                    variant="text"
                    className="text-primary-dark p-[16px] h-[44px] font-switzer text-[14px] font-[400] leading-[22px] bg-inherit hover:dark:!bg-[#303236] hover:bg-gray-100 w-full justify-start"
                  >
                    Test campaign
                  </Button>
                )}
              <Button
                onClick={() => navigate(`edit/${row?.original?._id}`)}
                variant="text"
                disabled={row.original.is_stopped || row.original.is_completed}
                className="text-primary-dark p-[16px] h-[44px] font-switzer text-[14px] font-[400] leading-[22px] bg-inherit hover:dark:!bg-[#303236] hover:bg-gray-100 w-full justify-start"
              >
                Edit campaign
              </Button>
              {row.original.campaign_creation_percent_complete === 100 &&
                row.original.is_active && (
                  <Button
                    onClick={pauseResumeCampaign}
                    variant="text"
                    disabled={
                      row.original.is_stopped || row.original.is_completed
                    }
                    className="text-primary-dark p-[16px] h-[44px] font-switzer text-[14px] font-[400] leading-[22px] bg-inherit hover:dark:!bg-[#303236] hover:bg-gray-100 w-full justify-start"
                  >
                    {row?.original?.is_paused
                      ? "Resume campaign"
                      : "Pause Campaign"}
                  </Button>
                )}
              <Button
                onClick={deleteCampaign}
                variant="text"
                className="text-primary-dark p-[16px] h-[44px] font-switzer text-[14px] font-[400] leading-[22px] bg-inherit hover:dark:!bg-[#303236] hover:bg-gray-100 w-full justify-start"
              >
                Delete campaign
              </Button>
            </>
          )}

          {page === "knowledgebase" && (
            <>
              {" "}
              <Button
                onClick={() => download(row?.original?.id)}
                variant="text"
                className="text-primary-dark p-[16px] h-[44px] font-switzer text-[14px] font-[400] leading-[22px] bg-inherit hover:dark:!bg-[#303236] hover:bg-gray-100 w-full justify-start"
              >
                View document
              </Button>
              <Button
                onClick={() => {
                  setIsOpen(false);
                  openModal({
                    view: (
                      <DeleteNumber
                        type="knowledgebase"
                        deleteFunction={deleteDocument}
                      />
                    ),
                  });
                }}
                variant="text"
                className="text-primary-dark p-[16px] h-[44px] font-switzer text-[14px] font-[400] leading-[22px] bg-inherit hover:dark:!bg-[#303236] hover:bg-gray-100 w-full justify-start"
              >
                Delete document
              </Button>
            </>
          )}
        </div>
      </Popover.Content>
    </Popover>
  );
}
